import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fundValuationDayFormFields } from '../../forms/fields/formFields';
import { fundValuationDayFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import {
  createFundValuationDay,
  updateFundValuationDay,
} from '../../redux/modules/fundValuationDay/fundValuationDay.actions';
import { fundValuationDaySelector } from '../../redux/modules/fundValuationDay/fundValuationDay.selectors';
import { FundValuationDay } from '../../types/fundValuationDay';

interface FundValuationDayDialogProps {
  open: boolean;
  initialValue: FundValuationDay | null;
  onClose: (isReload: boolean) => void;
}

export function FundValuationDayDialog({ open, initialValue, onClose }: FundValuationDayDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, resetForm } = useFormik({
    initialValues: {
      [fundValuationDayFormFields.nameEn]: '',
      [fundValuationDayFormFields.nameAr]: '',
      [fundValuationDayFormFields.descriptionEn]: '',
      [fundValuationDayFormFields.descriptionAr]: '',
      [fundValuationDayFormFields.expression]: '',
    },
    validationSchema: fundValuationDayFormSchema,
    onSubmit: (formValues) => {
      if (initialValue?.id) {
        dispatch(updateFundValuationDay({ id: initialValue.id, ...formValues }));
      } else {
        dispatch(createFundValuationDay(formValues));
      }
    },
  });
  const { createStatus, updateStatus, error } = useSelector(fundValuationDaySelector);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setValues({
      nameEn: initialValue?.nameEn || '',
      nameAr: initialValue?.nameAr || '',
      descriptionEn: initialValue?.descriptionEn || '',
      descriptionAr: initialValue?.descriptionAr || '',
      expression: initialValue?.expression || '',
    });
  }, [initialValue]);

  useEffect(() => {
    if (createStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Fund Valuation Day created!', { variant: 'success' });
    }
    if (createStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Fund Valuation Day creation error!', { variant: 'error' });
    }

    if (updateStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Fund Valuation Day updated!', { variant: 'success' });
    }
  }, [createStatus, updateStatus]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit fund Valuation Day' : 'Create a new Fund Valuation Day'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {error && <Alert severity="error">{error.message}</Alert>}
          Enter the Fund Valuation Day name
        </DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="nameEn"
            name={fundValuationDayFormFields.nameEn}
            label="Name En"
            value={values[fundValuationDayFormFields.nameEn]}
            onChange={handleChange}
            error={touched.nameEn && !!errors.nameEn}
            helperText={touched.nameEn && errors.nameEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            id="nameAr"
            name={fundValuationDayFormFields.nameAr}
            label="Name Ar"
            value={values[fundValuationDayFormFields.nameAr]}
            onChange={handleChange}
            error={touched.nameAr && !!errors.nameAr}
            helperText={touched.nameAr && errors.nameAr}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            id={fundValuationDayFormFields.descriptionEn}
            name={fundValuationDayFormFields.descriptionEn}
            label="Description En"
            value={values[fundValuationDayFormFields.descriptionEn]}
            onChange={handleChange}
            error={touched.descriptionEn && !!errors.descriptionEn}
            helperText={touched.descriptionEn && errors.descriptionEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            multiline
            id={fundValuationDayFormFields.descriptionAr}
            name={fundValuationDayFormFields.descriptionAr}
            label="Description Ar"
            value={values[fundValuationDayFormFields.descriptionAr]}
            onChange={handleChange}
            error={touched.descriptionAr && !!errors.descriptionAr}
            helperText={touched.descriptionAr && errors.descriptionAr}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id={fundValuationDayFormFields.expression}
            name={fundValuationDayFormFields.expression}
            label="Expression"
            value={values[fundValuationDayFormFields.expression]}
            onChange={handleChange}
            error={touched.expression && !!errors.expression}
            helperText={touched.expression && errors.expression}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={createStatus === fetchStatuses.pending}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
